import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengSelectDirective,
  SelectItem,
  UrlValidator,
} from '@web/shared/ui';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { EditorModule } from 'primeng/editor';
import { DatePicker } from 'primeng/datepicker';
import {
  AdminMultimediaImageUpload,
  MultimediaChooseOrUploadControlComponent,
} from '@web/shared/module/multimedia';
import { executeOrNull, Nillable, valueOrDefault } from '@web/utils';
import {
  AdminBannerDetailsForm,
  AdminBannerDetailsFormGroup,
} from '../../models/admin-banner-details-form';
import { Observable } from 'rxjs';
import { AdminBannerStatusProviderService } from '../../providers/admin-banner-status-provider.service';
import { AdminBannerStatus } from '../../enums/admin-banner-status';
import { Select } from 'primeng/select';
import { Button } from 'primeng/button';
import { BannerLocationType } from '../../../shared/enums/banner-location-type';
import { BannerLocationTypeProviderService } from '../../../shared/providers/banner-location-type-provider.service';

@Component({
  selector: 'app-admin-banner-details-form',
  imports: [
    Button,
    CommonModule,
    DatePicker,
    EditorModule,
    FormControlComponent,
    InputTextModule,
    MultimediaChooseOrUploadControlComponent,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    PrimengSelectDirective,
    ReactiveFormsModule,
    Select,
  ],
  templateUrl: './admin-banner-details-form.component.html',
  styleUrl: './admin-banner-details-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBannerDetailsFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);
  private readonly statusProvider = inject(AdminBannerStatusProviderService);
  private readonly locationProvider = inject(BannerLocationTypeProviderService);

  public readonly data = input<Nillable<AdminBannerDetailsForm>>();
  public readonly fixedLocation = input<Nillable<BannerLocationType>>(null);
  public readonly isEditContext = input<boolean>(false);
  public readonly allowedLocations = input<BannerLocationType[]>([
    BannerLocationType.HomePageHeader,
    BannerLocationType.SubscribePageHeader,
  ]);
  public readonly submitClickEvent = output<AdminBannerDetailsForm>();

  protected readonly form = this.buildForm();
  /* prettier-ignore */
  protected readonly locationSelectItems$ = this.locationSelectItemsSource();
  /* prettier-ignore */
  protected readonly statusSelectItems$ = this.statusSelectItemsSource();
  protected readonly valueOrDefault = valueOrDefault;

  public ngOnInit(): void {
    this.fillByData();
  }

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  private toData(): AdminBannerDetailsForm {
    const data = this.form.getRawValue();

    return new AdminBannerDetailsForm({
      fileId: data.file!.id,
      location: data.location!,
      smallFileId: data.smallFile?.id,
      status: data.status!,
      url: data.url,
      visibilityEndDate: data.visibilityEndDate,
      visibilityStartDate: data.visibilityStartDate,
    });
  }

  private buildForm(): AdminBannerDetailsFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      url: this.builder.control<Nillable<string>>(null, [UrlValidator.url]),
      file: this.builder.control<Nillable<AdminMultimediaImageUpload>>(null, [Validators.required]),
      smallFile: this.builder.control<Nillable<AdminMultimediaImageUpload>>(null),
      location: this.builder.control<Nillable<BannerLocationType>>(null, [Validators.required]),
      status: this.builder.control<Nillable<AdminBannerStatus>>(null, [Validators.required]),
      visibilityStartDate: this.builder.control<Nillable<Date>>(null),
      visibilityEndDate: this.builder.control<Nillable<Date>>(null),
    });
  }

  private fillByData(): void {
    const data = this.data();
    const fixedLocation = this.fixedLocation();

    /* prettier-ignore */
    this.form.patchValue({
      file: executeOrNull(data?.fileId, (id) => new AdminMultimediaImageUpload({id})),
      location: fixedLocation ?? data?.location,
      smallFile: executeOrNull(data?.smallFileId, (id) => new AdminMultimediaImageUpload({id})),
      url: data?.url,
      status: data?.status,
      visibilityEndDate: data?.visibilityEndDate,
      visibilityStartDate: data?.visibilityStartDate,
    });

    if (fixedLocation) {
      this.form.controls.location.disable();
    }
  }

  private locationSelectItemsSource(): Observable<
    SelectItem<BannerLocationType>[]
  > {
    return this.locationProvider.selectItemsFor(this.allowedLocations());
  }

  /* prettier-ignore */
  private statusSelectItemsSource(): Observable<SelectItem<AdminBannerStatus>[]> {
    return this.statusProvider.selectItems();
  }
}
